// Shared option lists for event attendance (must match backend enums in schemas/event.py).
export const ATTENDANCE_ROLES = [
  "Clinical Lead",
  "Clinical Deputy",
  "Finish Doctor",
  "Medical Team",
  "Response Team",
  "Event Control",
  "Incident Manager",
  "Mountain Safety",
  "Race Organiser",
];

// Where a role sorts when a list of people is read by role: the order above, which is
// the one the team thinks in (clinical first, then who is out on the course, then the
// non-medical roles) rather than alphabetical. Lives next to the list because the
// order *is* the list's order — reorder one and the other follows.
//
// An unset role, and any value the app no longer offers (the column is a free string
// and the data holds old ones), sorts last; the comparator below tie-breaks on the
// name, so those group together rather than scattering.
export const attendanceRoleRank = (role) => {
  const i = role ? ATTENDANCE_ROLES.indexOf(role) : -1;
  return i === -1 ? ATTENDANCE_ROLES.length : i;
};

// Surname first, which is how the Personnel roster and an event's people list are
// ordered; the displayed "Forename Surname" can't sort that way.
export const surnameKey = (p) => `${p?.surname || ""} ${p?.forename || ""}`.trim();

// Order a list of people on an event: by role, then by surname within each role. Rows
// carry `role` and a `surnameKey`. Shared because the same set of people is offered by
// three different controls — the allocation picker on a new incident and the share and
// reallocate dialogs — and a picker of colleagues that isn't in the order the event
// list uses reads as no order at all (attendances arrive unsorted from the API).
export const sortByAttendanceRole = (a, b) =>
  (attendanceRoleRank(a.role) - attendanceRoleRank(b.role))
  || (a.surnameKey || "").localeCompare(b.surnameKey || "", undefined, { sensitivity: "base" });

export const ATTENDANCE_STATUSES = [
  "Available",
  "Selected",
  "Contract Sent",
  "Contract Signed",
  "No Contract Needed",
];

// Days after an event ends that its attendance roles keep conferring permissions.
// Someone whose only access comes from being on an event therefore can't reach it
// (or its incidents) later. Mirrors ATTENDANCE_GRACE_DAYS in
// backend/app/core/authz.py, which enforces it.
export const ATTENDANCE_GRACE_DAYS = 2;

// The statuses that mean a person is actually committed to the event, rather than
// merely flagged Available (or not set at all). Must match ACTIVE_STATUSES in
// backend/app/core/permissions.py, which is also what makes an attendance role
// confer event-scoped permissions.
export const ACTIVE_ATTENDANCE_STATUSES = [
  "Selected",
  "Contract Sent",
  "Contract Signed",
  "No Contract Needed",
];

// The statuses whose attendance is compliance-checked. Narrower than
// ACTIVE_ATTENDANCE_STATUSES on purpose: that decides what a person may *do*,
// this decides what we hold on them, and someone merely Available or outside the
// contracting process is asked for nothing. Mirrors CHECKED_STATUSES in
// backend/app/routers/compliance.py, which is the enforcement point.
export const COMPLIANCE_CHECKED_STATUSES = [
  "Selected",
  "Contract Sent",
  "Contract Signed",
];

// How much of the incidents list to show. Purely a display filter: the server has
// already limited the list to what the reader may see at all
// (core/authz.incident_visibility), and this only chooses how much of that to put
// on screen. Widest last, so the dropdown reads narrow-to-wide.
//
// The three differ most for someone with a discovery-only event role (Medical
// Team, Response Team, Finish Doctor, Race Organiser): for them "All visible to
// me" adds the incidents they can see *only* because they share a participant with
// one of their own, which no narrower option matches.
export const INCIDENT_SCOPE_ALLOCATED = "allocated";
export const INCIDENT_SCOPE_INTERACTED = "interacted";
export const INCIDENT_SCOPE_ALL = "all";

export const INCIDENT_SCOPES = [
  { value: INCIDENT_SCOPE_ALLOCATED, label: "Allocated to me",
    help: "Allocated to you, personally or through one of your teams." },
  { value: INCIDENT_SCOPE_INTERACTED, label: "Interacted with",
    help: "Allocated to you, raised by you, shared with you, or with a note from you." },
  { value: INCIDENT_SCOPE_ALL, label: "All visible to me",
    help: "Everything your roles let you see." },
];

// Post-event wrap-up: the work done on an event's incidents once it is over,
// rather than while it is running. Each option narrows the list to the incidents
// one of those jobs is about — the patients who agreed their photographs could be
// used, the ones who need (and agreed to) a follow up, and the incidents the race
// organiser had a hand in and is owed a copy of.
//
// Single-select because the jobs are done one at a time, and because two of them
// answer different questions of the same list. The whole group — these filters and
// the exports beside them — is behind `Incidents.wrapUp`, Super Admin only.
//
// The first two are plain fields on the incident. The third is not answerable from
// the list at all (it needs the event's roster and its team membership), so it is
// asked of the server: GET /incidents/race-organiser, which returns the same set
// the report is drawn from. See core/incident_wrap_up.py.
export const WRAP_UP_NONE = "";
export const WRAP_UP_PHOTO_CONSENT = "photo-consent";
export const WRAP_UP_FOLLOWUP_CONSENT = "followup-consent";
export const WRAP_UP_RACE_ORGANISER = "race-organiser";

export const WRAP_UP_FILTERS = [
  { value: WRAP_UP_PHOTO_CONSENT, label: "Photo consent",
    help: "The patient agreed their images could be shared for teaching." },
  { value: WRAP_UP_FOLLOWUP_CONSENT, label: "Requires follow up",
    help: "We want to follow the patient up after the event, and they have consented to it." },
  { value: WRAP_UP_RACE_ORGANISER, label: "Race Organiser",
    help: "Raised by, or allocated to, a Race Organiser or a team with one in it." },
];

export const STATUS_COLOR = {
  Available: "default",
  Selected: "info",
  "Contract Sent": "warning",
  "Contract Signed": "success",
  "No Contract Needed": "secondary",
};

// --- Personnel ---
// Option lists for a personnel record. Stored as plain strings server-side, so
// these lists are the single source of the dropdown choices. Shared because both
// the Personnel page (its filters) and PersonnelDialog (the form) need them.

// Where a person stands with us. Set by the office on the personnel record and
// never shown on My details. Mirrors PERSONNEL_STATUSES in
// backend/app/schemas/personnel.py; a new record starts at Onboarding.
export const PERSONNEL_STATUSES = [
  { value: "Onboarding", hint: "Still providing the information needed to submit availability" },
  // Set automatically from Onboarding when the person's own save on My details
  // leaves nothing missing for availability (core/personnel_readiness.py).
  { value: "Onboard Complete", hint: "Has provided everything needed to submit availability" },
  { value: "Clear to Work", hint: "Everything needed to work an event is in place: references, Right to Work and DBS where required, qualifications in date" },
  { value: "Rejected", hint: "Applied, but we turned them down" },
  { value: "Left", hint: "Has worked for us, or at least completed onboarding, but no longer does" },
  { value: "Race Organiser", hint: "Not a member of staff; has access as a race organiser" },
  { value: "Ex Race Organiser", hint: "A previous race organiser, without current access" },
];
export const DEFAULT_PERSONNEL_STATUS = "Onboarding";

export const PERSONNEL_ROLES = [
  "Event Control", "Response", "First Aid", "HCP",
  "Medical Lead", "Medical 2IC", "Non Doctor Lead", "Team Lead",
  "Mountain Safety", "Mountain Safety Plus",
];

// "Doctor (training)" is retired — a doctor's grade (below) says it now, and
// infra/set-doctor-grades.py turned every holder into "Doctor". It's still
// tolerated wherever qualifications are read (HCP_QUALIFICATIONS, compliance),
// and the dialog offers it only to a record still holding it, so it can be unticked.
export const RETIRED_QUALIFICATIONS = ["Doctor (training)"];

// Registered with the GMC like a doctor (so a GMC number and Verify), but
// with no doctor's grade. A healthcare professional for compliance.
export const PHYSICIAN_ASSOCIATE = "Physician Associate";
// The qualifications that show the GMC number. Mirrors GMC_QUALS in
// backend/app/core/personnel_self_edit.py.
export const GMC_QUALIFICATIONS = ["Doctor", "Doctor (training)", PHYSICIAN_ASSOCIATE];

export const PERSONNEL_QUALIFICATIONS = [
  "Doctor",
  PHYSICIAN_ASSOCIATE,
  "Physiotherapist",
  "Paramedic",
  "Nurse",
  "First Aid",
  "RRMT",
  "FREC3",
  "FREC4",
  "WEMT",
  "ML",
  "WML",
  "MCI",
  "WMCI",
  "DBS",
  "Driving Licence",
  "Other",
];

// The two entries above that aren't clinical or mountain awards but are held the
// same way — ticked on the record, then evidenced. Named because each reveals its
// own fields: DBS a certificate and expiry (an ordinary CERT_QUALIFICATIONS row),
// Driving Licence a number, dates and two photographs of its own on the personnel
// record (PersonnelDialog's "Driving licence" section).
export const DBS_QUALIFICATION = "DBS";
export const DRIVING_LICENCE_QUALIFICATION = "Driving Licence";

// Qualifications that can only be held on top of others: you can't hold a winter
// award without its summer one. Listed closed rather than transitively (WMCI
// names all three, not just WML), because that's how the rule is stated. Mirrors
// QUALIFICATION_PREREQUISITES in backend/app/schemas/personnel.py, which rejects
// the save; this list only blocks the button so it's said before the request.
export const QUALIFICATION_PREREQUISITES = {
  WML: ["ML"],
  MCI: ["ML"],
  WMCI: ["ML", "WML", "MCI"],
};

// Qualifications that carry an (optional) certificate.
export const CERT_QUALIFICATIONS = ["First Aid", "RRMT", "FREC3", "FREC4", "WEMT",
                                    "ML", "WML", "MCI", "WMCI", DBS_QUALIFICATION];

// Of those, the ones whose certificate expires, so an expiry is worth asking for.
// The mountain qualifications don't — awarded once, held for life — so the
// compliance check for them is only that a certificate is on file. Mirrors
// _cert_problem in backend/app/routers/compliance.py, which takes a certificate
// with no expiry recorded at face value.
//
// A DBS certificate has no printed expiry either, but the team re-checks them on
// a cycle, so the date the record should be renewed by is asked for and held here.
export const EXPIRY_QUALIFICATIONS = ["First Aid", "RRMT", "FREC3", "FREC4", "WEMT",
                                      DBS_QUALIFICATION];

// The declarations of suitability a person makes on My details before saying
// they're available for an event. The wording lives only here; the record keeps
// when each box was ticked (`field`, e.g. personnel.declared_right_to_work_at),
// and `flag` is what PUT /personnel/self takes (DECLARATIONS in
// backend core/personnel_self_edit.py). Rewording one doesn't touch a date
// already recorded, so a material change needs everyone asked again.
export const DECLARATIONS = [
  {
    field: "declared_right_to_work_at",
    flag: "declare_right_to_work",
    title: "Right to work in the UK",
    text: "I confirm that I have the legal right to undertake the work for which I am applying "
      + "in the UK, and that I will be able to provide satisfactory evidence of my right to work "
      + "before undertaking any work for Shelter Stone.",
  },
  {
    field: "declared_suitability_at",
    flag: "declare_suitability",
    title: "Safeguarding and suitability",
    text: "I confirm that, to the best of my knowledge, there is no reason why I would be "
      + "unsuitable to work with children or adults at risk, or to undertake the role for which "
      + "I am applying. I understand that, where applicable to my role, my engagement will be "
      + "subject to satisfactory safeguarding and Disclosure and Barring Service (DBS) checks.",
  },
  {
    field: "declared_fitness_to_practise_at",
    flag: "declare_fitness_to_practise",
    title: "Fitness to practise",
    text: "I confirm that I am fit and suitable to undertake the role for which I am applying "
      + "and, where my role requires professional registration, that I am appropriately "
      + "registered and have no current restriction, suspension or condition on my practice "
      + "that would prevent me from undertaking the role. I agree to inform Shelter Stone if "
      + "this changes.",
  },
];

// Healthcare professionals: need indemnity + a recently-checked registration ID.
// Mirrors HCP_QUALS in backend/app/routers/compliance.py.
export const HCP_QUALIFICATIONS = ["Doctor", "Doctor (training)", PHYSICIAN_ASSOCIATE, "Nurse", "Paramedic",
                                   "Physiotherapist"];

// A doctor's grade. Mirrors GRADES in backend/app/core/doctor_grades.py, which
// validates it and judges each against the GMC register (served on Verify as
// grade_problems, so the rule itself lives only there).
export const SPECIALIST_REGISTER = "Specialist Register";
export const GP_REGISTER = "GP Register";
// Specialty and Specialist doctors: out of training, on neither register.
export const SAS_DOCTOR = "SAS Doctor";
export const DOCTOR_GRADES = [
  "FY1", "FY2", "FY3",
  "CT1", "CT2", "CT3",
  "ST1", "ST2", "ST3", "ST4", "ST5", "ST6", "ST7", "ST8",
  SAS_DOCTOR, SPECIALIST_REGISTER, GP_REGISTER,
];
// A doctor in training gives their educational supervisor as their referee,
// so the form prefills it. Mirrors TRAINING_REFERENCE_POSITION there.
export const TRAINING_REFERENCE_POSITION = "Educational Supervisor";
export const isTrainingGrade = (grade) =>
  !!grade && ![SAS_DOCTOR, SPECIALIST_REGISTER, GP_REGISTER].includes(grade);

export const CLOTHING_SIZES = [
  "Men S", "Men M", "Men L", "Men XL", "Men XXL",
  "Women S", "Women M", "Women L", "Women XL",
];

export const VEHICLES = ["None", "Car", "Campervan"];

export const INDEMNITY_PROVIDERS = [
  "MDU", "MDDUS", "MPS", "Glenham", "Saepio",
  "COP membership", "CSP membership", "RCN membership", "Other",
];

// --- Inventory ---
// Inventory type categories. Unlike most option lists here these ARE validated
// server-side — keep in step with the Category enum in
// backend/app/schemas/inventory_type.py, or creating a type will 422.
//
// A category decides how its items behave: the two lists below are the only
// difference between categories, and every other category is a plain item.
export const INVENTORY_CATEGORIES = ["Drugs", "Medical", "PPE", "Clothing", "Equipment", "Storage", "IT"];

// Chip colour per category — the category badge on the Types page, and the colour of
// each item's name pill on the Inventory page, so both read the same way.
export const INVENTORY_CATEGORY_COLOR = {
  Drugs: "error", // Red
  Medical: "success", // Green
  PPE: "warning", // Orange
  Clothing: "info", // Blue
  Equipment: "primary", // Dark green
  Storage: "default", // Light grey
  IT: "secondary", // Dark grey
};

// Items in these categories are batch-tracked stock: what an item holds is a list of
// supplier batches (batch number + expiry), each with a quantity, plus a required
// quantity on the item. One batch can be split across many bags, and every change to
// a quantity is a stock operation with a reason — a delivery, a move, a check, a
// drug given — recorded in the ledger. The item's own quantity/expiry are roll-ups
// computed server-side (sum, and soonest expiry). See backend/app/core/stock.py.
export const BATCHED_CATEGORIES = ["Drugs", "Medical"];

// The batched categories where every unit must be accounted for: a kit check that
// finds less (or more) than recorded needs a reason and is flagged for investigation.
// For the others a short count is simply taken as used. Keep in step with
// STRICT_CATEGORIES in backend/app/core/stock.py.
export const STRICT_STOCK_CATEGORIES = ["Drugs"];

// The part containers of a type play in batch-tracked stock (inventory_types.stock_role).
export const STOCK_ROLES = [
  { value: "store", label: "Stock cupboard",
    hint: "Deliveries are booked in here, and bags are topped up from here" },
  { value: "quarantine", label: "Pending disposal",
    hint: "Stock set aside to be disposed of — expired, damaged or recalled" },
];
export const STOCK_ROLE_LABEL = Object.fromEntries(STOCK_ROLES.map(r => [r.value, r.label]));

// --- Drugs types: the formulary fields (inventory_types.drug_*) ---
// Shown only for a Drugs type. Validated server-side — keep in step with the
// Literals in backend/app/schemas/inventory_type.py. Where a `hint` is given the form
// shows it beside the option, since the value itself is an abbreviation.
export const DRUG_ROUTES = [
  { value: "Buccal" },
  { value: "IM", hint: "Intramuscular injection" },
  { value: "Inhaled" },
  { value: "IV", hint: "Intravenous injection" },
  { value: "Nebulised" },
  { value: "Ocular" },
  { value: "Oral" },
  { value: "Oral rinse" },
  { value: "SC", hint: "Subcutaneous injection" },
  { value: "Sublingual" },
  { value: "Topical" },
];

export const DRUG_CLASSES = [
  "Analgesic", "Antacid", "Antianginal", "Antiarrhythmic", "Antibiotic",
  "Antidiarrhoeal", "Antiemetic", "Antifibrinolytic", "Antifungal", "Antihistamine",
  "Antimuscarinic", "Antiplatelet", "Antiseptic", "Barrier cream", "Bronchodilator",
  "Corticosteroid", "Diagnostic", "Glucose", "Inotrope", "Intravenous fluid",
  "Local anaesthetic", "Proton pump inhibitor", "Rehydration", "Skin adhesive",
  "Wound cleaning",
];

// In this order on purpose — least to most restricted, then devices.
export const DRUG_LEGAL_CATEGORIES = [
  { value: "Not licensed" },
  { value: "GSL", hint: "General sale list" },
  { value: "P", hint: "Pharmacy" },
  { value: "POM", hint: "Prescription only medicine" },
  { value: "POM (CD Sch 5)", hint: "POM controlled drug schedule 5" },
  { value: "Medical device" },
];

// Under what authority each group may give the drug. Unrecorded saves as "No".
export const DRUG_AUTHORITIES = [
  { value: "No" },
  { value: "Own" },
  { value: "PSD", hint: "Patient Specific Direction" },
];

// What a dose is recorded in — the drug type's single dose, and so every drug given
// of it (core/incident_drugs.dose). Keep in step with DoseUnit in
// backend/app/schemas/inventory_type.py.
export const DRUG_DOSE_UNITS = [
  "application", "drop(s)", "g", "mg", "micrograms", "ml", "puff(s)", "sachet(s)",
  "spray(s)", "tablet(s)",
];

// One drug_authority_<key> column each.
export const DRUG_AUTHORITY_GROUPS = [
  { key: "doctor", label: "Doctor" },
  { key: "nurse", label: "Nurse" },
  { key: "paramedic", label: "Paramedic" },
  { key: "physiotherapist", label: "Physiotherapist" },
  { key: "rrmt", label: "RRMT" },
  { key: "other_first_aid", label: "Other first aid (e.g. FREC4)" },
];

export const DRUGS_CATEGORY = "Drugs";

// What a type is called wherever it's shown: for a drug, name (common name)
// presentation — "Paracetamol (Calpol) 120 mg/5 ml suspension". Composed server-side
// (InventoryType.display_name) and served on every type; the fallback only covers a
// type object built in the browser.
export const typeLabel = (t) => t?.display_name || t?.name || "";

// The same composition, for the type form's preview of a name not yet saved. Keep in
// step with InventoryType.display_name in backend/app/models/inventory_type.py.
export const drugDisplayName = (t) => {
  const common = (t?.drug_common_name || "").trim();
  return [(t?.name || "").trim(), common ? `(${common})` : "",
          (t?.drug_presentation || "").trim()].filter(Boolean).join(" ");
};

// Why stock is set aside for disposal — offered, not enforced; anything may be typed.
export const QUARANTINE_REASONS = ["Expired", "Damaged", "Opened / part used", "Recalled by supplier"];

// Items in this category are the containers: bags, boxes, pouches. They can hold
// other items (which reference them via location_id), show a contents viewer and a
// kit check, and are what the Location picker offers. It's the category alone that
// makes an item a container — there is no per-type flag.
export const CONTAINER_CATEGORY = "Storage";

// What picking a category implies, for the type forms — since the category is now the
// only thing that decides how an item behaves, it's worth saying so at the point of choice.
export const categoryHint = (category) =>
  BATCHED_CATEGORIES.includes(category)
    ? "Stocked as batches: each batch has its own number, expiry and quantity, and every change is recorded"
    : category === CONTAINER_CATEGORY
      ? "A container: items of this type can hold other items"
      : "";

// --- Inventory allocations ---
// An item in someone's hands for an event. Unlike most option lists here these ARE
// validated server-side — keep in step with the AllocationStatus enum in
// backend/app/schemas/inventory_allocation.py.
//
// The order is the life of an allocation: written down, handed over, come back. Only
// "Returned" releases the item — while a row is at either of the other two, the item
// can't be allocated to anyone else (core/inventory_allocations.ensure_available).
export const ALLOCATION_STATUSES = ["Assigned", "Given", "Returned"];

// The two statuses that are reasoned about by name rather than just listed: handed
// over, and back again. Named rather than spelled out at each use — the printed
// checklist pre-ticks its boxes from them (exportAllocationsPdf.js) and the
// one-holder rule turns on the second (see isOpen in AllocationManager.jsx).
export const ALLOCATION_GIVEN = "Given";
export const ALLOCATION_RETURNED = "Returned";

export const ALLOCATION_STATUS_COLOR = {
  Assigned: "info",      // Blue — written down, not handed over yet
  Given: "warning",      // Orange — out with someone, still owed back
  Returned: "success",   // Green — back on the shelf
};

// --- Guidelines ---
// Guideline workflow status. Stored as a plain string server-side (see CLAUDE.md).
export const GUIDELINE_STATUSES = ["Draft", "Review", "Live", "Archived"];

export const GUIDELINE_STATUS_COLOR = {
  Draft: "default",
  Review: "warning",
  Live: "success",
  Archived: "secondary",
};

// The two curated link lists shown either side of the Shelter Stone guidelines.
// `kind` must match the values in backend/app/models/link.py.
export const GUIDELINE_LINK_LISTS = [
  {
    kind: "policy",
    heading: "Shelter Stone policies",
    empty: "No policy documents have been published yet.",
    addLabel: "Add policy",
  },
  {
    kind: "public",
    heading: "Public clinical guidelines",
    empty: "No public guidelines have been published yet.",
    addLabel: "Add public guideline",
  },
];

// One event's general-information links (documents / external links), managed on
// the admin Event dialog and shown to everyone on the event in "My events".
export const EVENT_LINK_LIST = {
  kind: "event",
  heading: "Information links",
  empty: "No documents or links have been added.",
  addLabel: "Add link",
};

export const GUIDELINES_DISCLAIMER =
  "These guidelines are intended to support clinical decision making and do not " +
  "replace clinical judgement. Staff should always act within their scope of " +
  "practice. If you have any concerns, please discuss with the Medical Director " +
  "at the event you are working on.";

// --- Incidents ---
// Option lists for the incident form. The backend stores these as free strings
// (so "Other > free text" sub-types and future edits need no migration); these
// lists are the single source of the dropdown choices.

export const INCIDENT_TYPES = [
  "Extraction (from the course)",
  "Gone to Ground",
  "Investigation",
  "Management Incident",
  "Management Policy",
  "Medical Major",
  "Medical Minor",
  "Missing",
  "Off Route",
  "Reported Emergency",
  "Retirement",
  "Testing",
  "Transport (from place of safety)",
];

export const PATIENT_SEXES = ["Male", "Female", "Prefer not to say", "Unknown"];

// Progress of an event participant's pre-event medical screening, in order.
// Must stay in step with the ScreeningStatus enum in schemas/event_participant.py.
// The first entry is the default — a participant is never without a status.
// "Watch List" is not a further stage of screening but its outcome: the entrant
// has been screened and needs watching during the event, which is why it is the
// only status carrying its own notes (see WATCH_LIST_STATUS below).
export const SCREENING_STATUSES = ["Not Required", "To Do", "Initiated", "Complete", "Watch List"];

// The status that reveals the Watch list notes field. Named rather than spelled
// out at each use so the string lives in one place.
export const WATCH_LIST_STATUS = "Watch List";

export const SCREENING_STATUS_COLOR = {
  "Not Required": "default",
  "To Do": "warning",
  "Initiated": "info",
  "Complete": "success",
  // Red, not because it is a failure but because it is the one status a
  // responder scanning the entry list needs to notice.
  "Watch List": "error",
};

// Ordered for display/sorting: the running order of an event's locations.
// ("On Course" trails — it's an incident location type rather than a fixed place.)
export const LOCATION_TYPES = [
  "Event Centre", "Start", "Overnight Camp", "Aid Station", "Water Point", "Finish", "On Course",
];

// Sort locations by their type (using LOCATION_TYPES order), then by name.
// Unknown/blank types sort last.
export const sortByLocationType = (a, b) => {
  const rank = (t) => {
    const i = LOCATION_TYPES.indexOf(t);
    return i === -1 ? LOCATION_TYPES.length : i;
  };
  const ra = rank(a.type);
  const rb = rank(b.type);
  if (ra !== rb) return ra - rb;
  return (a.name || "").localeCompare(b.name || "");
};

// How a pre-defined location is named in a summary breakdown, and the bucket for
// incidents that weren't at one. Both are mirrored by the backend
// (core/event_summaries._location_label / OTHER_LOCATION), which keys the
// multi-event comparison's tallies by exactly these strings — they live here
// rather than in a page because the single-event summary, the comparison report
// and its export all have to read the same.
export const OTHER_LOCATION = "Other";

export const locationSummaryLabel = (l) =>
  [l.name || "(unnamed)", l.type].filter(Boolean).join(" — ");

// The order a breakdown keyed by those labels is listed in: the places in the
// order the Event Locations page itself lists them (sortByLocationType), with
// Other last. `locations` is every location any event in scope has — for the
// comparison report that is the union across its columns, so an aid station only
// one year had still sits with the other aid stations.
export const locationSummaryOrder = (locations) => [
  ...new Set([...(locations || [])].sort(sortByLocationType).map(locationSummaryLabel)),
  OTHER_LOCATION,
];

// Marker colour per location type on the incidents map (components/IncidentMap.jsx).
// Unlike the other colour maps in this file these are CSS colour *literals*, not
// MUI palette keys: they are painted into Leaflet marker markup, which knows
// nothing of the theme. Adding a LOCATION_TYPES entry means adding one here too,
// or that type's pin falls back to grey.
export const LOCATION_TYPE_COLOR = {
  "Event Centre":   "#6a1b9a", // purple
  "Start":          "#2e7d32", // green
  "Overnight Camp": "#5d4037", // brown
  "Aid Station":    "#0277bd", // blue
  "Water Point":    "#0097a7", // teal
  "Finish":         "#212121", // near-black — the chequered-flag reading
  "On Course":      "#ef6c00", // orange
};

// A location whose type the app no longer offers (the column is a free string and
// the data holds old values), or which has none.
export const MAP_UNKNOWN_LOCATION_COLOR = "#757575";

// Incidents that aren't at one of the event's pre-defined locations. The same red
// the PDF exports title with, so the loose casualties are what the eye goes to.
// They are also drawn as a rounded square rather than a circle, so the
// distinction survives for a reader who can't separate two hues.
export const MAP_INCIDENT_COLOR = "#c81923";

// The attendance roles offered the sticky location default on a new incident: the
// pre-defined location they last filed one at is pre-filled. These are the roles
// that work from one place — a doctor at the finish, a medical team on an aid
// station — so the last location is nearly always the next one. Everyone else
// (Event Control, Incident Manager, Response Team, Mountain Safety) raises
// incidents wherever they happen, where a pre-filled location is more often wrong
// than right, so they get a blank one. Values must be ATTENDANCE_ROLES entries.
export const STICKY_LOCATION_ROLES = [
  "Clinical Lead", "Clinical Deputy", "Finish Doctor", "Medical Team",
];

// The attendance roles whose work is treating people: a new incident they raise
// starts as DEFAULT_CLINICAL_INCIDENT_TYPE, since nearly every one of theirs is a
// patient in front of them. Only ever a default for a blank field — the type is a
// dropdown like any other, and Medical Major is one selection away.
//
// The same four names as STICKY_LOCATION_ROLES, deliberately a separate list: that
// one is about working in one place, this one about being a clinician, and a role
// added to either shouldn't silently join the other. Values must be
// ATTENDANCE_ROLES entries, and the type an INCIDENT_TYPES entry.
export const CLINICAL_ATTENDANCE_ROLES = [
  "Clinical Lead", "Clinical Deputy", "Finish Doctor", "Medical Team",
];
export const DEFAULT_CLINICAL_INCIDENT_TYPE = "Medical Minor";

// What was deployed in response to the incident. Both are optional — blank means
// nothing recorded / not applicable. Stored as free strings server-side.
export const RESPONSE_TEAM_DEPLOYMENTS = [
  "Deployed",
  "MRT equivalent",
  "Local MRT interop",
  "Local MRT transfer",
];

export const MOUNTAIN_SAFETY_DEPLOYMENTS = ["Advised", "Sheltered", "Walked off"];

// How the patient left, and where to. Blank is not recorded, like the deployments
// above. They replaced a single "Ambulance transfer: Yes", which the 2026-10-15
// migration mapped to NHS Ambulance / A&E (local to event).
export const DISPOSAL_TRANSPORTS = [
  "Friends/Family Vehicle",
  "Event Vehicle",
  "Event Ambulance",
  "NHS Ambulance",
  "HEMS Helicopter",
  "SAR Helicopter",
];

export const DISPOSAL_DESTINATIONS = [
  "Primary Care (patient home)",
  "Minor Injuries Unit (local to patient home)",
  "Minor Injuries Unit (local to event)",
  "A&E (local to patient home)",
  "A&E (local to event)",
  "Major Trauma Centre (local to event)",
];

// Whether the patient was advised to retire, and what they did about it. Blank is
// not advised. A clinical field (core/clinical.py). "Yes" is not offered: it is
// what every incident advised back when this was a tickbox reads as, the outcome
// never having been recorded, and the form shows it as-is until one is picked.
// "…but continued against advice" raises a clinical report, matched by its exact
// text in core/clinical_report_triggers.py — reword both together.
export const ADVISED_TO_RETIRE_OPTIONS = [
  "Yes, and did so",
  "Yes, and did so but grudgingly",
  "Yes, but continued against advice",
];

// The patient's consent to treatment. A clinical field (core/clinical.py), required
// to close a Medical Major/Minor incident (routers/incident._ensure_closable). The
// names are mirrored in core/clinical_report_triggers.py, where a refusal or a
// patient unable to consent raises a clinical report — change both together.
// Anything but full consent needs the consent details, and `prompts` is what those
// details should cover — shown beside the box.
export const FULL_CONSENT = "Consents to treatment";
const DECLINES_PROMPTS = [
  "what was advised, and why;",
  "risks explained, and how the patient’s understanding was checked, including any translation used;",
  "your view of the patient’s capacity to make the decision;",
  "patient’s decision;",
  "safety-netting advice given.",
];
export const PATIENT_CONSENT_INFO = [
  { name: FULL_CONSENT, prompts: [] },
  { name: "Consents to some treatment, but declines some treatment or advice", prompts: DECLINES_PROMPTS },
  { name: "Declines all treatment or advice", prompts: DECLINES_PROMPTS },
  { name: "Unable to consent – treated in best interests", prompts: [
    "decision patient could not make, and why — which part of the capacity test they could not meet;",
    "steps taken to help the patient decide for themselves;",
    "why the treatment given was in the patient’s best interests (England and Wales) or of benefit to them (Scotland);",
    "whether, and when, the patient’s capacity was reassessed;",
    "when the Medical Director or Clinical Lead was informed, and any advice they gave.",
  ] },
];
export const PATIENT_CONSENT_OPTIONS = PATIENT_CONSENT_INFO.map(o => o.name);

/** Whether a consent option must be explained in the consent details. */
export const consentNeedsDetails = (consent) => !!consent && consent !== FULL_CONSENT;

// Below this age someone else's consent is recorded too ("Child consent given by").
export const CHILD_CONSENT_AGE = 16;

/** Whole years from a "YYYY-MM-DD" date of birth to `on` (a Date or ISO string,
 *  default now), or null when the DOB is missing or unreadable. */
export const ageFromDob = (dob, on = new Date()) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dob || "");
  const at = new Date(on);
  if (!m || Number.isNaN(at.getTime())) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  let age = at.getFullYear() - y;
  if (at.getMonth() + 1 < mo || (at.getMonth() + 1 === mo && at.getDate() < d)) age -= 1;
  return age;
};

/** Whether the patient is a child for consent: the recorded age, or the entrant's
 *  date of birth on the day of the incident, is under CHILD_CONSENT_AGE. Either is
 *  enough — the age is a snapshot typed in or copied, and may be blank. A blank age
 *  is not 0 (Number("") === 0), so it counts for nothing. */
export const isChildPatient = (age, dob, on) => {
  const n = age === "" || age == null ? null : Number(age);
  if (n != null && Number.isFinite(n) && n < CHILD_CONSENT_AGE) return true;
  const fromDob = ageFromDob(dob, on || new Date());
  return fromDob != null && fromDob < CHILD_CONSENT_AGE;
};

// The Medical details type, in the order to consider them: an injury with an
// external cause is Trauma before it is anything else. `use` is the guidance shown
// in the picker. The 2026-10-15 migration mapped the previous lists onto these.
export const INCIDENT_DETAIL_TYPE_INFO = [
  { name: "Trauma", use: "Any injury caused by an external force or a specific incident: fall, trip, slip, collision, rolled ankle." },
  { name: "MSK", use: "Musculoskeletal pain or injury without an external force, whether gradual (overuse) or sudden (e.g. hamstring strain)." },
  { name: "Feet", use: "Skin and nail problems of the feet: blisters, hot spots, maceration, toenails." },
  { name: "Skin", use: "Skin problems anywhere other than the feet." },
  { name: "Exertional & environmental", use: "Conditions caused by exercise or the weather: collapse, exhaustion, cramp, heat and cold illness, hyponatraemia." },
  { name: "Medical", use: "Illness by body system, including suspected viral illness." },
  { name: "Other", use: "None of the above — describe it." },
];
export const INCIDENT_DETAIL_TYPES = INCIDENT_DETAIL_TYPE_INFO.map(t => t.name);

// The sub-type that takes a free-text description (incidents.detail_other). Every
// type offers it, and it is type "Other"'s only sub-type, so the form sets it.
export const OTHER_SUBTYPE = "Other";

// Sub-types per detail type (alphabetical, Other last). A stored value no longer
// listed here — "Pain Management" under Medical and "Taping" under MSK, which
// the migration couldn't map — still shows as-is, but can't be picked.
export const INCIDENT_SUBTYPES = {
  Trauma: [
    "Contusion/haematoma",
    "Dislocation",
    "Eye injury",
    "Facial/dental injury",
    "Fracture",
    "Head injury",
    "Major wound",
    "Minor wound",
    "Multiple/major trauma",
    "Sprain",
    OTHER_SUBTYPE,
  ],
  MSK: [
    "Back pain",
    "Bone stress injury",
    "Flare of existing condition",
    "Knee pain",
    "Muscle soreness/stiffness",
    "Other joint pain",
    "Strain",
    "Tendinopathy",
    OTHER_SUBTYPE,
  ],
  Feet: [
    "Blister, blood",
    "Blister, extensive",
    "Blister, infected",
    "Blister, simple",
    "Hot spot/prevention",
    "Maceration",
    "Toenail problem",
    OTHER_SUBTYPE,
  ],
  Skin: [
    "Blister (not foot)",
    "Burn/scald",
    "Chafing",
    "Insect bite/sting",
    "Rash",
    "Skin infection",
    "Sunburn",
    "Tick bite",
    OTHER_SUBTYPE,
  ],
  "Exertional & environmental": [
    "Dehydration",
    "Exercise-associated collapse",
    "Exercise-associated hyponatraemia",
    "Exhaustion",
    "Heat exhaustion",
    "Heatstroke",
    "Hypothermia",
    "Muscle cramp",
    OTHER_SUBTYPE,
  ],
  Medical: [
    "Abdominal pain",
    "Alcohol/drug-related",
    "Allergic reaction",
    "Anaphylaxis",
    "Asthma/Bronchospasm",
    "Cardiac arrest",
    "Chest pain",
    "Diarrhoea",
    "Ear problem",
    "Eye problem",
    "Faint/syncope",
    "Headache",
    "Hyperglycaemia",
    "Hypoglycaemia",
    "Lower respiratory tract infection",
    "Mouth ulcer",
    "Nausea/vomiting",
    "Nosebleed",
    "Other gastrointestinal",
    "Other infection",
    "Other neurological",
    "Palpitations/Arrhythmia",
    "Psychological/anxiety",
    "Seizure",
    "Upper respiratory tract infection",
    "Urinary/gynaecological/haematuria",
    "Viral illness",
    OTHER_SUBTYPE,
  ],
  Other: [OTHER_SUBTYPE],
};

/** The sub-type as read: "Other" carries its description ("Other: wasp sting"). */
export const detailSubtypeLabel = (i) => {
  const sub = (i.detail_subtype || "").trim();
  const other = (i.detail_other || "").trim();
  return sub === OTHER_SUBTYPE && other ? `${OTHER_SUBTYPE}: ${other}` : sub;
};

// Safety netting: the categories of "what to look out for" advice a patient can be
// given, with the notes that belong beside each one so whoever ticks it can see
// what it covers. Zero or more are recorded per incident (incidents.safety_netting,
// a JSON array of these `name`s — so the notes can be reworded, and an option
// added, without touching stored data). Names are what's stored, exported and
// printed: renaming one leaves incidents carrying the old name, which still shows.
export const SAFETY_NETTING = [
  { name: "Biomechanical", notes: "field advice" },
  { name: "Circulation", notes: "swelling, capillary refill" },
  { name: "Taping skin care", notes: "irritation, tape removal" },
  { name: "Haematoma", notes: "numbness, worsening symptoms" },
  { name: "Back care",
    notes: "worsening of symptoms, increasing neurogenic pain or absence of sensation" },
  { name: "Cauda equina", notes: "red flags and to stop race and seek immediate care" },
  { name: "Wound infection", notes: "signs of infection" },
];

// The free-text option, kept out of the list above because it is a checkbox *and* a
// text box rather than one of the plain categories the list is mapped over.
//
// It is stored in the same array as the rest, as "Other: what was said". That needs
// no new column and no migration: `safety_netting` is a list of free strings, not an
// enum (schemas/incident.py takes `list[str]`), and every reader joins the list as it
// stands — the Excel export, both PDFs, the patient-history panel and the incident's
// own collapsed summary all print it correctly with no change. It's also how the
// qualifications export already renders its own "Other", so the phrasing is the app's.
export const SAFETY_NETTING_OTHER = "Other";
const OTHER_NETTING_PREFIX = `${SAFETY_NETTING_OTHER}: `;

// Whether a stored name is the Other option — ticked with nothing typed yet, or with.
export const isOtherNetting = (name) =>
  name === SAFETY_NETTING_OTHER || (name || "").startsWith(OTHER_NETTING_PREFIX);

// What to store for the Other option. Ticked with nothing typed stores the bare
// name, so the tick itself survives a save and reads back as ticked.
//
// The text is kept **verbatim**, spaces and all. It has to be: the stored value is
// the only copy while the box is being typed into, so every keystroke writes it and
// reads it back — trim here and the space in "keep it" is eaten the moment it's
// typed, making a space impossible to enter. normaliseNetting() tidies the ends
// once, on save.
export const otherNettingValue = (text) =>
  text ? `${OTHER_NETTING_PREFIX}${text}` : SAFETY_NETTING_OTHER;

// The text out of a stored safety-netting list, "" when Other isn't among it.
// Strips exactly the prefix — a greedy \s* here would eat a leading space the same
// way, so the two functions have to be exact inverses.
export const otherNettingText = (names) => {
  const found = (names || []).find(isOtherNetting);
  return found && found.startsWith(OTHER_NETTING_PREFIX)
    ? found.slice(OTHER_NETTING_PREFIX.length)
    : "";
};

// The list as it should be *stored*: the Other entry trimmed, and dropped back to
// the bare tick if that leaves nothing. Applied on save, which is the one moment the
// text has stopped changing.
export const normaliseNetting = (names) => (names || []).map(
  n => (isOtherNetting(n) ? otherNettingValue(otherNettingText([n]).trim()) : n));

export const INCIDENT_STATUSES = ["Ongoing", "Closed"];

// Incident types that can't be closed until their Medical details type and
// sub-type are filled in. Mirrors MEDICAL_INCIDENT_TYPES in
// backend/app/routers/incident.py, which enforces it. Incidents closed before this
// rule existed may be missing the details; the rule only applies when a status
// *changes* to Closed, so those stay editable.
export const MEDICAL_INCIDENT_TYPES = ["Medical Major", "Medical Minor"];

// Whether clinical review applies to this incident at all — an incident is
// "pending clinical review" when this holds and `clinical_review_at` is unset.
// Either the incident type is a medical one (the types that can't even be closed
// without their Medical details) or a Medical details type has been recorded, which
// covers anything treated under another type. A Management Incident with no
// clinical content is never awaiting review, and counting it only buried the ones
// that are.
//
// Shared by the Incidents page's "Pending clinical review" filter and the Event
// view's summary count, which must agree. Reads `detail_type`, which is blanked
// for a reader without Incidents.viewClinical — so both callers are already behind
// that permission.
export const needsClinicalReview = (i) =>
  MEDICAL_INCIDENT_TYPES.includes(i.incident_type) || !!(i.detail_type || "").trim();

// Incident review reminders. Interval choices (hours) are shared by one-off and
// recurring reminders; keep in sync with schemas/reminder.py INTERVAL_CHOICES.
export const REMINDER_INTERVAL_HOURS = [1, 2, 3, 4, 6, 12, 24];
export const REMINDER_MAX_OCCURRENCES = 10;

export const INCIDENT_STATUS_COLOR = {
  Ongoing: "warning",
  Closed: "success",
};

// Prompting helper text for the free-text "On course incident details" fields.
export const INCIDENT_DETAIL_PROMPTS = {
  with_whom: "Who are they with? What is the size of the group? Names and ages.",
  phone_status: "What is the battery on their phone, and what is the signal like?",
  alternative_phones: "Other numbers for the person or group if not already on the event database.",
  experience: "Of the person and/or group they are with.",
  weather: "Describe the weather conditions.",
  welfare: "Status of food, nutrition, clothing and equipment.",
};

// --- Feedback and complaints --------------------------------------------------
// Mirrors the Literals in backend schemas/feedback.py — change both together.
// Staff feedback also offers "Speaking Up"; the public form doesn't. In the order
// the form lists them, which its hint (components/FeedbackForm.jsx) follows.
export const STAFF_FEEDBACK_NATURES = ["Complaint", "Speaking Up", "Feedback", "Praise"];
export const FEEDBACK_NATURES = STAFF_FEEDBACK_NATURES.filter(n => n !== "Speaking Up");
export const FEEDBACK_STATUSES = ["Created", "Following Up", "Closed"];
export const FEEDBACK_STATUS_COLOR = {
  "Created": "warning",
  "Following Up": "info",
  "Closed": "default",
};
export const FEEDBACK_NATURE_COLOR = {
  "Complaint": "error",
  "Feedback": "info",
  "Praise": "success",
  "Speaking Up": "warning",
};
export const FEEDBACK_RESOLUTIONS = [
  "Policy change", "Kit list change", "Event specific learning",
  "Passed to Event Organiser", "Other",
];
// The location list's two entries that aren't one of the event's own locations.
export const FEEDBACK_LOCATION_CHOICES = ["On Course", "Other"];

// --- Clinical reports ---------------------------------------------------------
// Mirrors the Literals in backend schemas/clinical_report.py (and the lists in
// core/clinical_reports.py) — change all of them together. `hint` is display only.
export const CLINICAL_REPORT_TYPES = [
  { value: "Refusal of treatment or advice" },
  { value: "Continuing despite advice to retire" },
  { value: "Treatment of a patient who lacks capacity" },
  { value: "Use of restraint" },
  { value: "Treatment without valid consent" },
  { value: "Concern or complaint about consent, privacy or dignity" },
  { value: "Medication error or near miss" },
  { value: "Suspected adverse drug reaction" },
  { value: "Stock discrepancy, loss or theft" },
  { value: "Breach of confidentiality" },
  { value: "Safeguarding concern" },
  { value: "Patient safety incident",
    hint: "For example, an equipment failure, a delay in care or a missed diagnosis" },
  { value: "Staff injury or exposure",
    hint: "For example, a needlestick injury, a fall or a vehicle accident, splashing blood or fluids" },
  { value: "Other", hint: "Describe it below" },
];
export const CLINICAL_HARM_LEVELS = [
  { value: "None", hint: "No harm was caused, including near misses." },
  { value: "Low", hint: "Minimal harm, needing only extra observation or minor treatment." },
  { value: "Moderate", hint: "Significant but not permanent harm, or harm needing further treatment, such as a hospital visit." },
  { value: "Severe", hint: "Permanent harm, or harm that has shortened the patient's life expectancy." },
  { value: "Death", hint: "The incident caused or contributed to the patient's death." },
];
export const CLINICAL_HARM_COLOR = {
  None: "default", Low: "info", Moderate: "warning", Severe: "error", Death: "error",
};
export const CLINICAL_REPORT_STATUSES = ["Created", "Following Up", "Closed"];
export const CLINICAL_REPORT_STATUS_COLOR = FEEDBACK_STATUS_COLOR;
